import { useEffect, useState } from 'react';

export type UploadStatus = 'uploading' | 'success' | 'error';

export interface UploadEntry {
  id: string;
  label: string;
  progress: number;
  status: UploadStatus;
}

interface State {
  uploads: UploadEntry[];
}

const UPLOAD_REMOVE_DELAY = 4000;

let count = 0;
const genId = () => {
  count += 1;
  return count.toString();
};

const removeTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const listeners: Array<(state: State) => void> = [];
let memoryState: State = { uploads: [] };

const setState = (updater: (state: State) => State) => {
  memoryState = updater(memoryState);
  listeners.forEach((listener) => listener(memoryState));
};

const addUpload = (label: string) => {
  const id = genId();
  setState((state) => ({
    uploads: [...state.uploads, { id, label, progress: 0, status: 'uploading' }],
  }));
  return id;
};

const setProgress = (id: string, progress: number) => {
  setState((state) => ({
    uploads: state.uploads.map((upload) => (upload.id === id ? { ...upload, progress } : upload)),
  }));
};

const removeAfterDelay = (id: string) => {
  if (removeTimeouts.has(id)) return;
  const timeout = setTimeout(() => {
    removeTimeouts.delete(id);
    setState((state) => ({ uploads: state.uploads.filter((upload) => upload.id !== id) }));
  }, UPLOAD_REMOVE_DELAY);
  removeTimeouts.set(id, timeout);
};

const settleUpload = (id: string, status: 'success' | 'error') => {
  setState((state) => ({
    uploads: state.uploads.map((upload) =>
      upload.id === id ? { ...upload, status, progress: 100 } : upload,
    ),
  }));
  removeAfterDelay(id);
};

const completeUpload = (id: string) => settleUpload(id, 'success');
const failUpload = (id: string) => settleUpload(id, 'error');

export const useUploadQueue = () => {
  const [state, setLocalState] = useState<State>(memoryState);

  useEffect(() => {
    listeners.push(setLocalState);
    return () => {
      const index = listeners.indexOf(setLocalState);
      if (index > -1) listeners.splice(index, 1);
    };
  }, []);

  return state;
};

export const uploadQueue = { addUpload, setProgress, completeUpload, failUpload };

export const getUploadLabel = (body: FormData, fallback: string) => {
  const files = Array.from(body.values()).filter((value): value is File => value instanceof File);
  if (!files.length) return fallback;
  if (files.length === 1) return files[0].name;
  return `${files.length} files`;
};

export const trackedUpload = async <T extends { success?: boolean }>(
  label: string,
  send: (onProgress: (percent: number) => void) => Promise<T>,
): Promise<T> => {
  const id = addUpload(label);
  try {
    const resp = await send((percent) => setProgress(id, percent));
    if (resp?.success) completeUpload(id);
    else failUpload(id);
    return resp;
  } catch (err) {
    failUpload(id);
    throw err;
  }
};
